import { defineStore } from "pinia";
import { api, type Me } from "../api";
import { identifyPendo } from "../pendo";

export const useSession = defineStore("session", {
  state: () => ({
    me: null as Me | null,
    loading: true,
    error: null as string | null,
    /** In-flight load, so the router guard can wait for identity instead of racing it. */
    ready: null as Promise<void> | null,
  }),
  getters: {
    // Used only to decide what to show. The server decides what to allow -- every admin route is
    // enforced server-side, so tampering with this reveals nothing. isAdmin/isManager follow the
    // *effective* role (false/true while an Admin is viewing as a Manager).
    isAdmin: (s) => s.me?.is_admin ?? false,
    isManager: (s) => s.me?.is_manager ?? false,
    assignedTags: (s) => s.me?.assigned_tags ?? [],
    realIsAdmin: (s) => s.me?.real_is_admin ?? false,
    actingAs: (s) => s.me?.acting_as ?? null,
    displayName: (s) => s.me?.name ?? s.me?.email ?? "Signed in",
    // Dev-lockdown gate. Only the deployed dev environment ever sets this to false --
    // prod, local, and everywhere else always return true because rag/dev_gate.gate_active()
    // uses positive detection on the dev SPA domain marker. The `?? true` default is
    // defence in depth: if /api/me somehow fails or returns a shape without the field
    // (e.g. against a much older backend), the SPA treats the caller as allowed rather
    // than accidentally locking them out. The router guard reads this to bounce un-
    // allowlisted callers on the deployed dev environment to /dev-blocked.
    devAllowed: (s) => s.me?.dev_allowed ?? true,
  },
  actions: {
    load(): Promise<void> {
      if (this.ready) return this.ready;
      this.loading = true;
      this.error = null;
      this.ready = (async () => {
        try {
          this.me = await api.me();
          // The only place identity is known, so the only correct place to tell Pendo about it.
          // Covers first load and every View-as change, since reload() routes back through here.
          // It cannot throw (see frontend/src/pendo.ts), so it cannot land in the catch below and
          // report an analytics hiccup to the user as a failed sign-in.
          identifyPendo(this.me);
        } catch (e) {
          this.error = e instanceof Error ? e.message : String(e);
        } finally {
          this.loading = false;
        }
      })();
      return this.ready;
    },
    /** Drop the cached /api/me so a View-as change is picked up on the next load. */
    reload(): Promise<void> {
      this.ready = null;
      return this.load();
    },
  },
});
